import { useRef, useState, useEffect } from 'react';
import { PenLine, RotateCcw, Check, X, Clock } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignaturePad({ documentType, documentId, onSigned, signerName }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingSignatures, setExistingSignatures] = useState([]);

  useEffect(() => {
    if (documentType && documentId) {
      fetchSignatures();
    }
  }, [documentType, documentId]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#F3F4F6';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // Draw signature line
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#273239';
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#F3F4F6';
  }, []);

  const fetchSignatures = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/signatures/${documentType}/${documentId}`,
        { withCredentials: true }
      );
      setExistingSignatures(data);
    } catch (err) {
      console.error(err);
    }
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const endDraw = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    // Redraw signature line
    ctx.setLineDash([4, 4]);
    ctx.strokeStyle = '#273239';
    ctx.beginPath();
    ctx.moveTo(20, rect.height - 30);
    ctx.lineTo(rect.width - 20, rect.height - 30);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = '#F3F4F6';
    setHasSignature(false);
  };

  const saveSignature = async () => {
    if (!hasSignature) {
      toast.error('Please draw your signature first');
      return;
    }
    setSaving(true);
    try {
      const signatureData = canvasRef.current.toDataURL('image/png');
      const { data } = await axios.post(`${API_URL}/api/signatures/sign`, {
        document_type: documentType,
        document_id: documentId,
        signature_data: signatureData
      }, { withCredentials: true });

      toast.success(`Document signed at ${data.timestamp}`);
      clearCanvas();
      fetchSignatures();
      if (onSigned) onSigned(data);
    } catch (err) {
      toast.error('Failed to save signature');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="signature-pad">
      {/* Existing Signatures */}
      {existingSignatures.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Signatures</p>
          {existingSignatures.map((sig) => (
            <div key={sig.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted border border-border">
              <Check className="w-4 h-4 text-[#A8E6CF]" />
              <div className="flex-1">
                <p className="text-xs text-foreground">{sig.signer_name} ({sig.signer_role})</p>
                <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Clock className="w-3 h-3" />{sig.timestamp}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Canvas */}
      <div className="relative">
        <p className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
          <PenLine className="w-3 h-3" />Draw your signature below
        </p>
        <canvas
          ref={canvasRef}
          className="w-full h-32 rounded-lg bg-muted border border-border cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid="signature-canvas"
        />
        {signerName && (
          <p className="text-[10px] text-muted-foreground mt-1 text-right">{signerName}</p>
        )}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={clearCanvas}
          className="border-border text-muted-foreground" data-testid="clear-signature-btn">
          <RotateCcw className="w-3 h-3 mr-1" />Clear
        </Button>
        <Button size="sm" className="btn-primary" onClick={saveSignature}
          disabled={!hasSignature || saving} data-testid="save-signature-btn">
          {saving ? 'Saving...' : 'Sign Document'}
        </Button>
      </div>
    </div>
  );
}
